/**
 * Program Guide client — resolves the active station slug and fetches
 * the public schedule from `/api/public/program-guide/by-slug/{slug}`.
 *
 * Shared by Guide.jsx, GuideReminders.jsx, and the admin page so all
 * three stay in sync.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LAST_SLUG_KEY = "bf_program_guide_last_slug";

/** Resolve the station slug from the URL search params or last-known
 * value in localStorage. Returns null if none can be resolved. */
export function resolveSlugFromUrl(search) {
  try {
    const params = new URLSearchParams(search || "");
    const fromQuery = params.get("station");
    if (fromQuery) {
      try { localStorage.setItem(LAST_SLUG_KEY, fromQuery); } catch { /* storage blocked */ }
      return fromQuery;
    }
    return localStorage.getItem(LAST_SLUG_KEY) || null;
  } catch {
    return null;
  }
}

export function rememberSlug(slug) {
  try { if (slug) localStorage.setItem(LAST_SLUG_KEY, slug); } catch { /* storage blocked */ }
}

/** Fetch active station for the logged-in user (returns null if not
 * authenticated or no station). Used as a slug fallback. */
export async function fetchMyStationSlug() {
  try {
    const r = await fetch(`${API_BASE}/api/stations/me`, { credentials: "include" });
    if (!r.ok) return null;
    const d = await r.json();
    if (!d || !d.name) return null;
    const slug = (d.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    return slug || d.station_id;
  } catch {
    return null;
  }
}

/** Fetch the public schedule for a station slug. Optional `day` arg
 * filters by day-of-week (mon/tue/wed/thu/fri/sat/sun). Returns
 * `{station, channels, active_day}` or throws. */
export async function fetchPublicGuide(slug, day) {
  const qs = day ? `?day=${encodeURIComponent(day)}` : "";
  const r = await fetch(`${API_BASE}/api/public/program-guide/by-slug/${encodeURIComponent(slug)}${qs}`);
  if (!r.ok) {
    if (r.status === 404) throw new Error("Station not found");
    throw new Error("Failed to load schedule");
  }
  return r.json();
}

/** Find a program by id across all channels — pure utility. */
export function findProgramByIdInChannels(channels, id) {
  for (const c of channels || []) {
    const p = (c.programs || []).find((x) => x.id === id);
    if (p) return { ...p, channelId: c.id, channelName: c.name, channelType: c.type, color: c.color };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// "Tune in when it starts" — persistent next-airing notifications
// ─────────────────────────────────────────────────────────────────────────────
const TUNE_IN_KEY = "bf_program_tune_ins";
const ALL_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function _loadTuneIns() {
  try {
    const raw = localStorage.getItem(TUNE_IN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function _saveTuneIns(items) {
  try { localStorage.setItem(TUNE_IN_KEY, JSON.stringify(items)); } catch { /* quota */ }
}

/** Compute next UTC timestamp (ms) when a program will start, based on
 * its days_of_week + start_time. Returns null if no match in the next 7 days. */
export function nextOccurrenceMs(program) {
  if (!program?.start_time) return null;
  const days = (program.days_of_week && program.days_of_week.length) ? program.days_of_week : ALL_DAYS;
  const [hh, mm] = program.start_time.split(":").map(Number);
  const dayMap = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 };
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const candidate = new Date(now);
    candidate.setUTCDate(now.getUTCDate() + i);
    candidate.setUTCHours(hh, mm, 0, 0);
    const dayCode = Object.keys(dayMap).find((k) => dayMap[k] === candidate.getUTCDay());
    if (!days.includes(dayCode)) continue;
    if (candidate.getTime() > now.getTime()) return candidate.getTime();
  }
  return null;
}

export function listTuneIns() {
  return _loadTuneIns();
}

export function hasTuneIn(programId) {
  return _loadTuneIns().some((t) => t.program_id === programId);
}

/** Add a tune-in. Returns the computed fire_at timestamp, or null
 * if no next-occurrence could be resolved. */
export function addTuneIn({ program_id, station_slug, station_name, title, fire_at_ms }) {
  if (!fire_at_ms || fire_at_ms < Date.now()) return null;
  const cur = _loadTuneIns().filter((t) => t.program_id !== program_id);
  cur.push({ program_id, station_slug, station_name: station_name || "", title, fire_at_ms });
  _saveTuneIns(cur);
  return fire_at_ms;
}

export function removeTuneIn(programId) {
  _saveTuneIns(_loadTuneIns().filter((t) => t.program_id !== programId));
}

/** Garbage-collect expired tune-ins (fire_at in the past). */
export function gcTuneIns() {
  const now = Date.now();
  const fresh = _loadTuneIns().filter((t) => t.fire_at_ms > now);
  _saveTuneIns(fresh);
  return fresh;
}
